import React, { useState, useEffect } from 'react';
import {
    Modal,
    Card,
    Button,
    Space,
    Tag,
    Typography,
    message,
    Alert,
    Descriptions,
    Spin,
    Popconfirm,
    Tooltip,
    Divider,
    Select
} from 'antd';
import {
    SafetyCertificateOutlined,
    CopyOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    KeyOutlined
} from '@ant-design/icons';
import dnsService from '../../services/dnsService';
import DnssecRolloverPanel, { DnssecKeyTags } from '../../../shared/components/DnssecRolloverPanel';

const { Text, Paragraph } = Typography;
const { Option } = Select;

const DnsDnssec = ({ visible, onClose, zoneId, zoneName }) => {
    const [loading, setLoading] = useState(false);
    const [dnssecStatus, setDnssecStatus] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [selectedAlgorithm, setSelectedAlgorithm] = useState('ECDSAP256SHA256');

    const algorithms = [
        { value: 'ECDSAP256SHA256', label: 'ECDSA P-256 (Algorithm 13) - 推荐' },
        { value: 'ECDSAP384SHA384', label: 'ECDSA P-384 (Algorithm 14)' },
        { value: 'RSASHA256', label: 'RSA SHA-256 (Algorithm 8)' },
        { value: 'RSASHA512', label: 'RSA SHA-512 (Algorithm 10)' },
    ];

    const fetchDnssecStatus = async () => {
        if (!zoneId) return;
        setLoading(true);
        try {
            const result = await dnsService.getDnssecStatus(zoneId);
            if (result.success) {
                setDnssecStatus(result.data);
            } else {
                message.error(result.error || '获取DNSSEC状态失败');
            }
        } catch (error) {
            message.error('获取DNSSEC状态失败: ' + (error.message || error.error));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible && zoneId) {
            fetchDnssecStatus();
        }
    }, [visible, zoneId]);

    const handleEnable = async () => {
        setActionLoading(true);
        try {
            const result = await dnsService.enableDnssec(zoneId, selectedAlgorithm);
            if (result.success) {
                message.success('DNSSEC已启用');
                await fetchDnssecStatus();
            } else {
                message.error(result.error || '启用DNSSEC失败');
            }
        } catch (error) {
            message.error('启用DNSSEC失败: ' + (error.message || error.error));
        } finally {
            setActionLoading(false);
        }
    };

    const handleDisable = async () => {
        setActionLoading(true);
        try {
            const result = await dnsService.disableDnssec(zoneId);
            if (result.success) {
                message.success('DNSSEC已禁用');
                await fetchDnssecStatus();
            } else {
                message.error(result.error || '禁用DNSSEC失败');
            }
        } catch (error) {
            message.error('禁用DNSSEC失败: ' + (error.message || error.error));
        } finally {
            setActionLoading(false);
        }
    };

    const handleRegenerate = async () => {
        setActionLoading(true);
        try {
            const result = await dnsService.regenerateDnssec(zoneId, selectedAlgorithm);
            if (result.success) {
                message.success(result.data?.rolled === 'zsk' ? 'ZSK 轮换已开始，DS 不变' : 'DNSSEC密钥已重新生成');
                await fetchDnssecStatus();
            } else {
                message.error(result.error || '重新生成密钥失败');
            }
        } catch (error) {
            message.error('重新生成密钥失败: ' + (error.message || error.error));
        } finally {
            setActionLoading(false);
        }
    };

    const handleRollover = async (action) => {
        setActionLoading(true);
        try {
            const result = await dnsService.dnssecRollover(zoneId, action);
            if (result.success) {
                message.success('已更新');
                await fetchDnssecStatus();
            } else {
                message.error(result.error || '操作失败');
            }
        } catch (error) {
            message.error('操作失败: ' + (error.error || error.message));
        } finally {
            setActionLoading(false);
        }
    };
    const rolling = !!dnssecStatus?.rollover && dnssecStatus.rollover.phase !== 'done';

    const copyToClipboard = (text, label) => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            message.success(`${label}已复制到剪贴板`);
        }).catch(() => {
            message.error('复制失败，请手动复制');
        });
    };

    const renderDsRecords = () => {
        if (!dnssecStatus?.ds_records || dnssecStatus.ds_records.length === 0) {
            return <Text type="secondary">暂无DS记录</Text>;
        }

        return (
            <div style={{ marginTop: 16 }}>
                <Divider orientation="left" style={{ fontSize: 14 }}>
                    <KeyOutlined /> DS 记录 (用于在注册商配置)
                </Divider>
                {dnssecStatus.ds_records.map((ds, index) => (
                    <Card
                        key={index}
                        size="small"
                        style={{ marginBottom: 12, background: '#fafafa' }}
                    >
                        <Descriptions column={1} size="small">
                            <Descriptions.Item label="Key Tag">
                                <Text code>{ds.key_tag}</Text>
                                <Tooltip title="复制">
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<CopyOutlined />}
                                        onClick={() => copyToClipboard(String(ds.key_tag), 'Key Tag')}
                                    />
                                </Tooltip>
                            </Descriptions.Item>
                            <Descriptions.Item label="算法">
                                <Text code>{ds.algorithm}</Text>
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    ({ds.algorithm === 13 ? 'ECDSAP256SHA256' :
                                      ds.algorithm === 14 ? 'ECDSAP384SHA384' :
                                      ds.algorithm === 8 ? 'RSASHA256' :
                                      ds.algorithm === 10 ? 'RSASHA512' :
                                      `Algorithm ${ds.algorithm}`})
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="摘要类型">
                                <Text code>{ds.digest_type}</Text>
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    ({ds.digest_type === 2 ? 'SHA-256' :
                                      ds.digest_type === 1 ? 'SHA-1' :
                                      ds.digest_type === 4 ? 'SHA-384' :
                                      `Type ${ds.digest_type}`})
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="摘要">
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Paragraph
                                        copyable={{ text: ds.digest }}
                                        style={{
                                            margin: 0,
                                            wordBreak: 'break-all',
                                            fontFamily: 'monospace',
                                            fontSize: 12
                                        }}
                                    >
                                        {ds.digest}
                                    </Paragraph>
                                </div>
                            </Descriptions.Item>
                        </Descriptions>
                        <Divider style={{ margin: '8px 0' }} />
                        <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>完整DS记录格式:</Text>
                            <div style={{
                                background: '#f5f5f5',
                                padding: 8,
                                borderRadius: 4,
                                marginTop: 4,
                                fontFamily: 'monospace',
                                fontSize: 11,
                                wordBreak: 'break-all'
                            }}>
                                <Text code>
                                    {zoneName}. IN DS {ds.key_tag} {ds.algorithm} {ds.digest_type} {ds.digest}
                                </Text>
                                <Tooltip title="复制完整DS记录">
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<CopyOutlined />}
                                        onClick={() => copyToClipboard(
                                            `${zoneName}. IN DS ${ds.key_tag} ${ds.algorithm} ${ds.digest_type} ${ds.digest}`,
                                            'DS记录'
                                        )}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    </Card>
                ))}
            </div>
        );
    };

    return (
        <Modal
            title={
                <Space>
                    <SafetyCertificateOutlined />
                    DNSSEC 管理
                    <Tag color="blue">{zoneName}</Tag>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            width={750}
            footer={[
                <Button key="close" onClick={onClose}>
                    关闭
                </Button>
            ]}
        >
            <Spin spinning={loading}>
                {dnssecStatus ? (
                    <div>
                        {/* 状态卡片 */}
                        <Card size="small" style={{ marginBottom: 16 }}>
                            <Space size="large" wrap>
                                <div>
                                    <Text type="secondary">DNSSEC 状态</Text>
                                    <div style={{ marginTop: 8 }}>
                                        {dnssecStatus.enabled ? (
                                            <Tag color="success" icon={<CheckCircleOutlined />}>
                                                已启用
                                            </Tag>
                                        ) : (
                                            <Tag color="default" icon={<CloseCircleOutlined />}>
                                                未启用
                                            </Tag>
                                        )}
                                    </div>
                                </div>
                                {dnssecStatus.enabled && dnssecStatus.algorithm && (
                                    <div>
                                        <Text type="secondary">签名算法</Text>
                                        <div style={{ marginTop: 8 }}>
                                            <Tag color="blue">{dnssecStatus.algorithm}</Tag>
                                        </div>
                                    </div>
                                )}
                                {dnssecStatus.enabled && dnssecStatus.key_tag && (
                                    <div>
                                        <Text type="secondary">密钥</Text>
                                        <div style={{ marginTop: 8 }}>
                                            {Array.isArray(dnssecStatus.keys) && dnssecStatus.keys.length
                                                ? <DnssecKeyTags keys={dnssecStatus.keys} />
                                                : <Text code>{dnssecStatus.key_tag}</Text>}
                                        </div>
                                    </div>
                                )}
                                {dnssecStatus.zone_id && (
                                    <div>
                                        <Text type="secondary">Zone ID</Text>
                                        <div style={{ marginTop: 8 }}>
                                            <Text code>{dnssecStatus.zone_id}</Text>
                                        </div>
                                    </div>
                                )}
                            </Space>
                        </Card>

                        {/* 算法选择 */}
                        {!dnssecStatus.enabled && (
                            <Card size="small" style={{ marginBottom: 16 }}>
                                <Space>
                                    <Text>签名算法:</Text>
                                    <Select
                                        value={selectedAlgorithm}
                                        onChange={setSelectedAlgorithm}
                                        style={{ width: 300 }}
                                    >
                                        {algorithms.map(algo => (
                                            <Option key={algo.value} value={algo.value}>
                                                {algo.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Space>
                            </Card>
                        )}

                        {/* 操作按钮 */}
                        <Space style={{ marginBottom: 16 }} wrap>
                            {!dnssecStatus.enabled ? (
                                <Popconfirm
                                    title="启用 DNSSEC"
                                    description={
                                        <div>
                                            <p>确定要为此区域启用 DNSSEC 吗？</p>
                                            <p>将使用算法: <strong>{selectedAlgorithm}</strong></p>
                                        </div>
                                    }
                                    onConfirm={handleEnable}
                                    okText="确定"
                                    cancelText="取消"
                                >
                                    <Button
                                        type="primary"
                                        icon={<SafetyCertificateOutlined />}
                                        loading={actionLoading}
                                    >
                                        启用 DNSSEC
                                    </Button>
                                </Popconfirm>
                            ) : (
                                <>
                                    <Popconfirm
                                        title="禁用 DNSSEC"
                                        description={
                                            <div>
                                                <p>确定要禁用 DNSSEC 吗？</p>
                                                <p style={{ color: '#ff4d4f' }}>
                                                    警告：禁用前请确保用户已在注册商处删除 DS 记录！
                                                </p>
                                            </div>
                                        }
                                        onConfirm={handleDisable}
                                        okText="确定禁用"
                                        okButtonProps={{ danger: true }}
                                        cancelText="取消"
                                        icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                                    >
                                        <Button
                                            danger
                                            loading={actionLoading}
                                        >
                                            禁用 DNSSEC
                                        </Button>
                                    </Popconfirm>
                                    <Popconfirm
                                        title={selectedAlgorithm === dnssecStatus?.algorithm ? '轮换 ZSK' : '更换算法'}
                                        description={selectedAlgorithm === dnssecStatus?.algorithm
                                            ? '预发布轮换 ZSK（约 1 小时后自动切换），DS 不变'
                                            : <span style={{ color: '#faad14' }}>将重建 KSK+ZSK（{selectedAlgorithm}），用户必须更新 DS</span>}
                                        onConfirm={handleRegenerate}
                                        okText="确定"
                                        cancelText="取消"
                                        icon={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
                                        disabled={rolling && selectedAlgorithm === dnssecStatus?.algorithm}
                                    >
                                        <Button
                                            icon={<ReloadOutlined />}
                                            loading={actionLoading}
                                            disabled={rolling && selectedAlgorithm === dnssecStatus?.algorithm}
                                        >
                                            重新生成密钥
                                        </Button>
                                    </Popconfirm>
                                    <Popconfirm
                                        title="轮换 KSK"
                                        description="用户需在注册商添加新 DS"
                                        onConfirm={() => handleRollover('start_ksk')}
                                        okText="确定"
                                        cancelText="取消"
                                        disabled={rolling}
                                    >
                                        <Button icon={<KeyOutlined />} loading={actionLoading} disabled={rolling}>
                                            轮换 KSK
                                        </Button>
                                    </Popconfirm>
                                    <Select
                                        value={selectedAlgorithm}
                                        onChange={setSelectedAlgorithm}
                                        style={{ width: 280 }}
                                        size="small"
                                    >
                                        {algorithms.map(algo => (
                                            <Option key={algo.value} value={algo.value}>
                                                {algo.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </>
                            )}
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={fetchDnssecStatus}
                                loading={loading}
                            >
                                刷新状态
                            </Button>
                        </Space>

                        {dnssecStatus.enabled && dnssecStatus.rollover && (
                            <Alert type="info" showIcon style={{ marginBottom: 16 }} message="密钥轮换"
                                description={<DnssecRolloverPanel status={dnssecStatus} onAction={handleRollover} loading={actionLoading} />} />
                        )}

                        {/* 启用状态下显示DS记录 */}
                        {dnssecStatus.enabled && renderDsRecords()}

                        {/* 配置说明 */}
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginTop: 16 }}
                            message="DNSSEC 管理说明"
                            description={
                                <ol style={{ paddingLeft: 20, marginBottom: 0 }}>
                                    <li>启用 DNSSEC 后，系统会自动生成签名密钥并签名所有记录</li>
                                    <li>用户需要将 DS 记录配置到域名注册商处</li>
                                    <li>DS 记录通常需要 24-48 小时在全球生效</li>
                                    <li>禁用 DNSSEC 前，必须确保用户已删除注册商处的 DS 记录</li>
                                    <li>推荐使用 ECDSA P-256 算法，兼容性好且性能优秀</li>
                                </ol>
                            }
                        />
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin />
                        <div style={{ marginTop: 16 }}>
                            <Text type="secondary">加载中...</Text>
                        </div>
                    </div>
                )}
            </Spin>
        </Modal>
    );
};

export default DnsDnssec;
